import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { reportError } from '../utils/errorHandling.js';

function Navbar() {
    try {
        const [isOpen, setIsOpen] = React.useState(false);
        const [scrolled, setScrolled] = React.useState(false);
        const [isVisible, setIsVisible] = React.useState(true);
        const [lastScrollY, setLastScrollY] = React.useState(0);
        const navigate = useNavigate();
        const { user, handleLogout } = useAuth();

        React.useEffect(() => {
            const handleScroll = () => {
                const currentScrollY = window.scrollY;
                
                // Set scrolled state for background changes
                setScrolled(currentScrollY > 10);
                
                // Handle navbar visibility based on scroll direction
                if (currentScrollY > lastScrollY && currentScrollY > 100) {
                    // Scrolling down - hide navbar
                    setIsVisible(false);
                } else if (currentScrollY < lastScrollY) {
                    // Scrolling up - show navbar
                    setIsVisible(true);
                }
                
                setLastScrollY(currentScrollY);
            };
            
            window.addEventListener('scroll', handleScroll, { passive: true });
            return () => window.removeEventListener('scroll', handleScroll);
        }, [lastScrollY]);

        React.useEffect(() => {
            try {
                // Simplified class toggling to avoid issues
                if (isOpen) {
                    document.body.className += ' mobile-nav-active';
                } else {
                    document.body.className = document.body.className.replace(/\bmobile-nav-active\b/g, '');
                }
                console.log('Mobile nav state:', isOpen ? 'open' : 'closed');
            } catch (err) {
                console.error('Error toggling mobile nav class:', err);
            }
            
            // Cleanup function
            return () => {
                try {
                    document.body.className = document.body.className.replace(/\bmobile-nav-active\b/g, '');
                } catch (err) {
                    console.error('Error in navbar cleanup:', err);
                }
            };
        }, [isOpen]);

        const handleLogoutClick = async () => {
            await handleLogout();
            navigate('/');
        };

        // Handle menu item clicks and close mobile menu
        const handleMenuItemClick = (callback) => {
            setIsOpen(false);
            if (typeof callback === 'function') {
                callback();
            }
        };

        // Add some debug logging
        console.log('Rendering navbar with user:', user ? `${user.email} (${user.role})` : 'Not logged in');
        console.log('User object:', user);
        
        // Create a more robust and visible navbar
        return (
            <nav 
                data-name="navbar" 
                className={`fixed w-full z-50 transition-all duration-500 ${
                    scrolled 
                        ? 'bg-white/75 backdrop-blur-sm shadow-lg py-2' 
                        : 'bg-transparent py-4'
                }`} 
                style={{
                    transform: `translateY(${isVisible ? '0' : '-100%'})`,
                }}
            >
                <div className="container mx-auto px-4">
                    <div className="flex justify-between items-center h-16">
                        <div data-name="logo" className="flex-shrink-0 flex items-center">
                            <Link to="/" className="flex items-center">
                                {/* Primary image with fallback */}
                                <div className="relative">
                                    {/* Try multiple paths for the logo */}
                                    <img 
                                        src={window.location.origin + "/assets/images/logo.png"} 
                                        alt="Sustainable Computing Network Logo"
                                        className="h-10"
                                        style={{ 
                                            cursor: 'pointer',
                                            display: 'block',
                                            minHeight: '40px',
                                            minWidth: '40px'
                                        }}
                                        onError={(e) => {
                                            console.log("Primary logo failed to load, trying fallback path");
                                            e.target.onerror = null;
                                            // Try the root path logo
                                            e.target.src = window.location.origin + "/scn-logo.png";
                                            
                                            // If that fails too, show the fallback text
                                            e.target.addEventListener('error', () => {
                                                console.log("All logo paths failed, showing fallback text");
                                                e.target.style.display = 'none';
                                                document.getElementById('scn-fallback-text').style.display = 'flex';
                                            });
                                        }}
                                    />
                                    
                                    {/* Fallback text logo only shown if image fails to load */}
                                    <div 
                                        id="scn-fallback-text"
                                        className={`${scrolled ? 'text-green-600' : 'text-white'} font-bold text-xl absolute inset-0 items-center justify-center`} 
                                        style={{textShadow: '0 1px 2px rgba(0,0,0,0.1)', display: 'none'}}
                                    >
                                        SCN
                                    </div>
                                </div>
                            </Link>
                        </div>
                        
                        <div className="hidden md:block">
                            <div className="ml-10 flex items-center space-x-8">
                                <Link 
                                    to="/mission" 
                                    className={`${scrolled ? 'text-gray-800' : 'text-white'} hover:text-green-600 transition-colors duration-300`}
                                >
                                    Our Mission
                                </Link>
                                <Link 
                                    to="/partner" 
                                    className={`${scrolled ? 'text-gray-800' : 'text-white'} hover:text-green-600 transition-colors duration-300`}
                                >
                                    Partner with Us
                                </Link>
                                <Link 
                                    to="/esg-platform" 
                                    className={`${scrolled ? 'text-gray-800' : 'text-white'} hover:text-green-600 transition-colors duration-300`}
                                >
                                    ESG Platform
                                </Link>
                                <Link 
                                    to="/llm" 
                                    className={`${scrolled ? 'text-gray-800' : 'text-white'} hover:text-green-600 transition-colors duration-300`}
                                >
                                    Sequoia A.I. <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full ml-1">New</span>
                                </Link>
                                {user ? (
                                    <div className="relative inline-block text-left">
                                        <div className="flex items-center space-x-4">
                                            <Link 
                                                to="/account" 
                                                className={`${scrolled ? 'text-gray-800' : 'text-white'} hover:text-green-600 transition-colors duration-300`}
                                            >
                                                My Account
                                            </Link>
                                            {user.role === 'admin' && (
                                                <>
                                                    <Link 
                                                        to="/admin" 
                                                        className={`${scrolled ? 'text-gray-800' : 'text-white'} hover:text-green-600 transition-colors duration-300`}
                                                    >
                                                        Admin Panel
                                                    </Link>
                                                    <Link 
                                                        to="/partner-portal" 
                                                        className={`${scrolled ? 'text-gray-800' : 'text-white'} hover:text-green-600 transition-colors duration-300`}
                                                    >
                                                        Partner Portal
                                                    </Link>
                                                </>                                                    )}
                                                    {user.role === 'partner' && (
                                                        <Link 
                                                            to="/partner-portal" 
                                                            className={`${scrolled ? 'text-gray-800' : 'text-white'} hover:text-green-600 transition-colors duration-300`}
                                                        >
                                                            Partner Portal
                                                        </Link>
                                                    )}
                                            <button 
                                                onClick={handleLogoutClick} 
                                                className={`${scrolled ? 'text-gray-800' : 'text-white'} hover:text-green-600 transition-colors duration-300`}
                                            >
                                                Logout
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center space-x-4">
                                        <Link 
                                            to="/login" 
                                            className={`${scrolled ? 'text-gray-800' : 'text-white'} hover:text-green-600 transition-colors duration-300`}
                                        >
                                            Login
                                        </Link>
                                        <Link 
                                            to="/register" 
                                            className={`${scrolled ? 'text-gray-800' : 'text-white'} hover:text-green-600 transition-colors duration-300`}
                                        >
                                            Register
                                        </Link>
                                    </div>
                                )}
                                <Link 
                                    to="/donate" 
                                    className={`btn btn-primary ${!scrolled ? 'btn-transparent' : ''}`}
                                    onClick={() => handleMenuItemClick()}
                                >
                                    Donate Now
                                </Link>
                            </div>
                        </div>
                        
                        <div className="md:hidden">
                            <button
                                onClick={() => setIsOpen(!isOpen)}
                                className={`${scrolled ? 'text-gray-800' : 'text-white'} hover:text-green-600 transition-colors duration-300 p-2 rounded-full ${!scrolled ? 'bg-green-500/10' : ''}`}
                                aria-label="Toggle navigation menu"
                            >
                                <i className={`fas ${isOpen ? 'fa-times' : 'fa-bars'} text-2xl`}></i>
                            </button>
                        </div>
                    </div>
                    
                    {isOpen && (
                        <div className="md:hidden bg-white/85 backdrop-blur-md rounded-lg shadow-lg mt-2 p-4 absolute left-0 right-0 mx-4 border border-gray-200/50 animate-fadeIn">
                            <div className="space-y-3">
                                <Link 
                                    to="/mission" 
                                    className="block px-3 py-2 text-gray-800 hover:text-green-600 transition-colors duration-300 w-full text-left"
                                    onClick={() => handleMenuItemClick()}
                                >
                                    Our Mission
                                </Link>
                                <Link 
                                    to="/partner" 
                                    className="block px-3 py-2 text-gray-800 hover:text-green-600 transition-colors duration-300 w-full text-left"
                                    onClick={() => handleMenuItemClick()}
                                >
                                    Partner with Us
                                </Link>
                                <Link 
                                    to="/esg-platform" 
                                    className="block px-3 py-2 text-gray-800 hover:text-green-600 transition-colors duration-300 w-full text-left"
                                    onClick={() => handleMenuItemClick()}
                                >
                                    ESG Platform
                                </Link>
                                <Link 
                                    to="/llm" 
                                    className="block px-3 py-2 text-gray-800 hover:text-green-600 transition-colors duration-300 w-full text-left"
                                    onClick={() => handleMenuItemClick()}
                                >
                                    Sequoia A.I. <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full ml-1">New</span>
                                </Link>
                                {user ? (
                                    <>
                                        <Link 
                                            to="/account" 
                                            className="block px-3 py-2 text-gray-800 hover:text-green-600 transition-colors duration-300 w-full text-left"
                                            onClick={() => handleMenuItemClick()}
                                        >
                                            My Account
                                        </Link>
                                        {user.role === 'admin' && (
                                            <>
                                                <Link 
                                                    to="/admin" 
                                                    className="block px-3 py-2 text-gray-800 hover:text-green-600 transition-colors duration-300 w-full text-left"
                                                    onClick={() => handleMenuItemClick()}
                                                >
                                                    Admin Panel
                                                </Link>
                                                <Link 
                                                    to="/partner-portal" 
                                                    className="block px-3 py-2 text-gray-800 hover:text-green-600 transition-colors duration-300 w-full text-left"
                                                    onClick={() => handleMenuItemClick()}
                                                >
                                                    Partner Portal
                                                </Link>
                                            </>
                                        )}
                                        {user.role === 'partner' && (
                                            <Link 
                                                to="/partner-portal" 
                                                className="block px-3 py-2 text-gray-800 hover:text-green-600 transition-colors duration-300 w-full text-left"
                                                onClick={() => handleMenuItemClick()}
                                            >
                                                Partner Portal
                                            </Link>
                                        )}
                                        <button 
                                            onClick={handleLogoutClick} 
                                            className="block px-3 py-2 text-gray-800 hover:text-green-600 transition-colors duration-300 w-full text-left"
                                        >
                                            Logout
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <Link 
                                            to="/login" 
                                            className="block px-3 py-2 text-gray-800 hover:text-green-600 transition-colors duration-300 w-full text-left"
                                            onClick={() => handleMenuItemClick()}
                                        >
                                            Login
                                        </Link>
                                        <Link 
                                            to="/register" 
                                            className="block px-3 py-2 text-gray-800 hover:text-green-600 transition-colors duration-300 w-full text-left"
                                            onClick={() => handleMenuItemClick()}
                                        >
                                            Register
                                        </Link>
                                    </>
                                )}
                                <Link 
                                    to="/donate" 
                                    className="block px-4 py-3 text-white bg-green-600 hover:bg-green-500 transition-colors duration-300 w-full text-center font-medium rounded-md mt-2"
                                    onClick={() => handleMenuItemClick()}
                                >
                                    Donate Now <i className="fas fa-heart ml-1"></i>
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </nav>
        );
    } catch (error) {
        console.error('Navbar error:', error);
        reportError(error);
        return null;
    }
}

window.Navbar = Navbar;

export default Navbar;
